// ─────────────────────────────────────────────────────────────────────────────
// Grievance export — one grievance as a printable page or a Word document.
//
// WHY THIS EXISTS. §483.10(j)(4)(iv) requires evidence demonstrating the result
// of every grievance to be kept three years, and §483.10(j)(4)(i) requires a
// written decision back to the resident. Both of those are paper. A surveyor
// asks for the file on a named resident and a screen is not an answer; neither
// is a screenshot with half the fields cut off.
//
// ONE DOCUMENT, TWO DELIVERIES. The page below is built once and used for both
// outputs, so the Word file and the printed copy can never drift into saying
// different things about the same grievance — which, for a record that has to
// survive three years in a binder, is the failure that actually matters.
//
// ON THE .doc FORMAT. This writes Word-compatible HTML with an Office document
// properties block, not a zipped .docx. Word, Pages and Google Docs all open it
// and it carries real document metadata including the Author. It needs no
// library, which is the whole reason: a document you can produce with nothing
// installed is one that still works when the build is three versions old.
//
// ON AUTHORSHIP. The author is the person who created the record — not the
// facility, not the software. Everyone who contributed documentation is named
// underneath with what they contributed. Employees NAMED IN a grievance are
// deliberately NOT in that block: they are the subject of a complaint, not
// contributors to it, and a document that blurs the two is a document that
// accuses someone by layout.
// ─────────────────────────────────────────────────────────────────────────────

import {
  CATEGORIES, STATUSES, categoryLabel, departmentLabel, statusLabel, complainantLabel,
  daysToClose,
} from "./grievanceModel";

// ── escaping ────────────────────────────────────────────────────────────────
// Every value below is facility-entered free text — a narrative, a name, an
// outcome. It is written into an HTML document, so it is escaped on the way in.
// A resident surname with an ampersand should not be able to break the page,
// and a pasted narrative should never be able to do worse.
function esc(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
// Free text that should keep its line breaks.
function escPara(v) {
  return esc(v).replace(/\r\n|\r|\n/g, "<br>");
}

const NOT_RECORDED = '<span class="na">Not recorded</span>';
function orNa(v) {
  const s = v === null || v === undefined ? "" : String(v).trim();
  return s ? esc(s) : NOT_RECORDED;
}

function longDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return String(iso);
  // UTC, matching fmtDate in _shared — a date-only value parses as UTC midnight
  // and formatting it locally would shift it a day back for US users. A
  // grievance dated one day earlier than it was is a records problem.
  return d.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
}

/**
 * Everyone who put something into this record, and what.
 *
 * The first entry is the author. `employeesMentioned` is NOT here on purpose —
 * see the header note.
 */
export function grievanceContributors(r) {
  const out = [];
  const seen = new Set();
  const add = (name, role) => {
    const n = String(name || "").trim();
    if (!n) return;
    const key = n.toLowerCase();
    if (seen.has(key)) {
      // Same person in a second role — keep one line and join the roles, rather
      // than listing them twice as though two people worked the file.
      const existing = out.find((e) => e.name.toLowerCase() === key);
      if (existing && !existing.roles.includes(role)) existing.roles.push(role);
      return;
    }
    seen.add(key);
    out.push({ name: n, roles: [role] });
  };
  add(r.filedBy, "Created this record");
  add(r.assignedTo, "Grievance Official / assigned");
  for (const a of r.actionNotes || []) add(a?.addedBy, "Added follow-up documentation");
  return out;
}

/** The person the document is authored by, or null when nothing was captured. */
export function grievanceAuthor(r) {
  const c = grievanceContributors(r);
  return c.length ? c[0].name : null;
}

/** A filesystem-safe basename, no extension. */
export function grievanceFileBase(r) {
  const who = r.complainantType === "anonymous"
    ? "Anonymous"
    : (r.residentName || "Unnamed");
  const parts = ["Grievance", r.id || "", who, r.dateReceived || ""];
  return parts
    .map((p) => String(p).replace(/[^A-Za-z0-9._-]+/g, "-"))
    .filter(Boolean)
    .join("_")
    .replace(/-+/g, "-")
    .slice(0, 120);
}

// ── the document ────────────────────────────────────────────────────────────

function row(label, value) {
  return `<tr><th>${esc(label)}</th><td>${value}</td></tr>`;
}

function section(title, inner) {
  return `<div class="sec"><h2>${esc(title)}</h2>${inner}</div>`;
}

/**
 * The grievance as a complete document.
 *
 * @param {object} r            the grievance record
 * @param {object} opts
 * @param {string} [opts.facilityName]  overrides the record's own label
 * @param {string} [opts.generatedBy]   who pressed the button, for the footer
 * @param {boolean} [opts.forWord]      emit the Office properties block
 * @param {boolean} [opts.autoPrint]    open the print dialog on load
 */
export function buildGrievanceDocument(r, opts = {}) {
  const { facilityName, generatedBy, forWord = false, autoPrint = false } = opts;
  const contributors = grievanceContributors(r);
  const author = contributors.length ? contributors[0].name : "";
  const d = daysToClose(r);
  const cat = CATEGORIES[r.category] || {};
  const st = STATUSES[r.status] || {};
  const facility = facilityName || r.facilityName || "";

  // ANONYMOUS MEANS ANONYMOUS. §483.10(j)(4)(i) gives the right to file without
  // giving a name; a printed copy that quietly fills the gap with "Unnamed
  // resident" in the same place a name would go invites someone to write one
  // in. Say which it is.
  const complainantLine = r.complainantType === "anonymous"
    ? '<span class="anon">Filed anonymously — no name recorded, by right under §483.10(j)(4)(i)</span>'
    : orNa(r.residentName);

  const title = `Grievance Record ${r.id ? "— " + r.id : ""}`.trim();

  const officeProps = forWord ? `
<!--[if gte mso 9]><xml>
 <o:DocumentProperties>
  <o:Title>${esc(title)}</o:Title>
  <o:Author>${esc(author)}</o:Author>
  <o:LastAuthor>${esc(generatedBy || author)}</o:LastAuthor>
  <o:Company>${esc(facility)}</o:Company>
  <o:Subject>Resident grievance — 42 CFR 483.10(j) / F585</o:Subject>
 </o:DocumentProperties>
</xml><![endif]-->` : "";

  const contributorRows = contributors.length
    ? contributors.map((c, i) => `<tr>
        <th>${esc(c.name)}${i === 0 ? ' <span class="tag">AUTHOR</span>' : ""}</th>
        <td>${esc(c.roles.join(" · "))}</td>
      </tr>`).join("")
    : `<tr><td colspan="2" class="na">No contributor was recorded on this grievance.</td></tr>`;

  const notes = (r.actionNotes || []).length
    ? `<table class="notes">${(r.actionNotes || []).map((a) => `<tr>
        <th>${orNa(longDate(a?.date) || a?.date)}<br><span class="by">${orNa(a?.addedBy)}</span></th>
        <td>${escPara(a?.note)}</td>
      </tr>`).join("")}</table>`
    : `<p class="na">No follow-up notes were recorded.</p>`;

  const employees = (r.employeesMentioned || []).filter(Boolean);
  const attachments = (r.attachments || []).map((a) => (a && a.name) || a).filter(Boolean);

  const notified = r.residentNotificationSent === true
    ? "Yes"
    : r.residentNotificationSent === false
      ? '<span class="bad">No — required under §483.10(j)(4)(i)</span>'
      : NOT_RECORDED;

  const body = `
<div class="head">
  <div class="brand">SurvAIHealth · Compliance Suite</div>
  <h1>Grievance Record</h1>
  <div class="sub">${esc(facility)}${r.facilityCcn ? " · CCN " + esc(r.facilityCcn) : ""}${r.id ? " · " + esc(r.id) : ""}</div>
  <div class="cite">Maintained under 42 CFR §483.10(j) — Resident Rights, Grievances (F585).
    Evidence demonstrating the result of a grievance must be retained no less than three years from the decision.</div>
</div>

${section("Grievance Details", `<table>
  ${row("Resident / complainant", complainantLine)}
  ${row("Reported by", esc(complainantLabel(r.complainantType) || "") || NOT_RECORDED)}
  ${row("Relation to resident", orNa(r.relationToResident))}
  ${row("Date received", orNa(longDate(r.dateReceived)))}
  ${row("Date of incident", orNa(longDate(r.incidentDate)))}
  ${row("Date filed", orNa(longDate(r.filedDate)))}
  ${row("Category", esc(categoryLabel(r.category) || "") || NOT_RECORDED)}
  ${row("Department", esc(departmentLabel(r.department) || "") || NOT_RECORDED)}
  ${row("Grievance Official / assigned to", orNa(r.assignedTo))}
</table>`)}

${section("Narrative as reported", `<div class="para">${escPara(r.narrative) || NOT_RECORDED}</div>
${employees.length ? `<div class="mentioned"><span class="mlabel">Employees named in this grievance</span>
  ${employees.map((e) => `<span class="mchip">${esc(e)}</span>`).join(" ")}
  <div class="mnote">Named by the complainant. Being named is not a finding.</div></div>` : ""}`)}

${section("Investigation and follow-up", `<div class="para">${escPara(r.investigationNotes) || NOT_RECORDED}</div>
<h3>Follow-up entries</h3>${notes}`)}

${section("Resolution", `<table>
  ${row("Status", esc(statusLabel(r.status) || "") || NOT_RECORDED)}
  ${row("Resolution date", orNa(longDate(r.resolutionDate)))}
  ${row("Days to close", d == null ? NOT_RECORDED : esc(`${d} day${d === 1 ? "" : "s"}`))}
  ${row("Resident / representative notified of the decision", notified)}
  ${row("Notification date", orNa(longDate(r.residentNotificationDate)))}
</table>
<h3>Outcome</h3><div class="para">${escPara(r.outcome) || NOT_RECORDED}</div>`)}

${section("Documented by", `<table class="who">${contributorRows}</table>
<div class="whonote">The author is the person who created this record. Anyone who added
follow-up documentation is listed with what they contributed.</div>`)}

${attachments.length ? section("Associated files", `<ul>${attachments.map((a) => `<li>${esc(a)}</li>`).join("")}</ul>
<div class="whonote">Referenced by name. The files themselves are not part of this printout.</div>`) : ""}

<div class="foot">
  <div class="conf">CONFIDENTIAL — contains resident information. Handle, store and dispose of under the
    facility's privacy policy and 45 CFR Parts 160 and 164.</div>
  <div>Printed ${esc(longDate(new Date().toISOString()))}${generatedBy ? " by " + esc(generatedBy) : ""} from the SurvAIHealth Grievance Log.
    ${author ? "Record created by " + esc(author) + "." : ""}</div>
</div>`;

  // Print rules worth keeping: a grievance runs to two pages often enough that
  // a section split across the break, or a narrative orphaned from its heading,
  // is the normal case rather than the edge one.
  const css = `
  @page { size: letter; margin: 0.9in 0.8in; }
  body { font-family: Calibri, Carlito, Arial, sans-serif; color: #1a1a1a; font-size: 11pt; line-height: 1.5; margin: 0; }
  .head { border-bottom: 2px solid #0f4c81; padding-bottom: 10px; margin-bottom: 18px; }
  .brand { font-size: 8.5pt; letter-spacing: .08em; text-transform: uppercase; color: #0f4c81; font-weight: bold; }
  h1 { font-size: 19pt; margin: 4px 0 2px; color: #0f172a; }
  .sub { font-size: 10pt; color: #444; }
  .cite { font-size: 8.5pt; color: #555; margin-top: 7px; line-height: 1.45; }
  .sec { margin-bottom: 17px; page-break-inside: avoid; }
  h2 { font-size: 11.5pt; color: #0f4c81; margin: 0 0 6px; padding-bottom: 3px; border-bottom: 1px solid #c8d6e3; page-break-after: avoid; }
  h3 { font-size: 10pt; color: #333; margin: 11px 0 4px; page-break-after: avoid; }
  table { width: 100%; border-collapse: collapse; margin: 0; }
  th { text-align: left; vertical-align: top; width: 38%; padding: 4px 10px 4px 0; font-weight: normal; color: #555; font-size: 10pt; }
  td { vertical-align: top; padding: 4px 0; font-size: 10.5pt; }
  table.who th { width: 42%; font-weight: bold; color: #0f172a; }
  table.notes th { width: 26%; font-size: 9.5pt; }
  table.notes .by { color: #666; font-size: 9pt; }
  .para { white-space: pre-wrap; font-size: 10.5pt; }
  .na { color: #888; font-style: italic; }
  .bad { color: #b3261e; font-weight: bold; }
  .anon { color: #555; font-style: italic; }
  .tag { font-size: 7.5pt; letter-spacing: .06em; background: #0f4c81; color: #fff; padding: 1px 5px; vertical-align: middle; }
  .mentioned { margin-top: 9px; }
  .mlabel { font-size: 8.5pt; text-transform: uppercase; letter-spacing: .05em; color: #666; display: block; margin-bottom: 3px; }
  .mchip { border: 1px solid #ccc; padding: 1px 7px; font-size: 9.5pt; }
  .mnote, .whonote { font-size: 8.5pt; color: #666; margin-top: 5px; }
  ul { margin: 4px 0 0 18px; padding: 0; font-size: 10.5pt; }
  .foot { border-top: 1px solid #c8d6e3; margin-top: 22px; padding-top: 8px; font-size: 8.5pt; color: #555; }
  .conf { font-weight: bold; color: #b3261e; margin-bottom: 3px; }`;

  const wordNs = forWord
    ? ' xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"'
    : "";
  const meta = author ? `<meta name="author" content="${esc(author)}">` : "";
  const script = autoPrint
    ? "<script>window.onload=function(){setTimeout(function(){window.print();},120);};<\/script>"
    : "";

  return `<!DOCTYPE html><html${wordNs}><head><meta charset="utf-8">
<title>${esc(title)}</title>${meta}${officeProps}
<style>${css}</style></head><body>${body}${script}</body></html>`;
}

/**
 * Open the grievance in a print window. The browser's own dialog offers
 * "Save as PDF" on every desktop platform, which is a real PDF with selectable
 * text — better than anything we would rasterise ourselves.
 *
 * Returns null on success, or a message to show the user.
 */
export function printGrievance(r, opts = {}) {
  const html = buildGrievanceDocument(r, { ...opts, forWord: false, autoPrint: true });
  const w = window.open("", "_blank");
  // A blocked popup fails silently otherwise, and the person presses the button
  // again and again with nothing happening.
  if (!w) return "Your browser blocked the print window. Allow pop-ups for this site, then try again.";
  w.document.write(html);
  w.document.close();
  return null;
}

/**
 * Download the grievance as a Word document. The Author field is the person
 * who created the record, so File → Info in Word shows them rather than
 * whoever happened to press the button.
 */
export function downloadGrievanceDoc(r, opts = {}) {
  const html = buildGrievanceDocument(r, { ...opts, forWord: true, autoPrint: false });
  // The BOM matters: without it Word guesses the encoding and mangles any
  // non-ASCII character in a resident's name.
  const blob = new Blob(["﻿", html], { type: "application/msword" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${grievanceFileBase(r)}.doc`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoked on the next tick — revoking synchronously races the download in
  // Safari and produces an empty file.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
  return null;
}
